import { useState, useCallback } from 'react';
import { message } from 'antd';
import { j30cApi, LabelData, LookupRequest, PrintLabel } from '../../../services/j30cApi';

export interface J30CState {
  loading: boolean;
  labelData: LabelData | null;
  printLabels: PrintLabel[] | null;
  queried: boolean;
  notFound: boolean;
}

export interface J30CActions {
  lookupProduct: (req: LookupRequest) => Promise<void>;
  doPrint: (copies: number) => Promise<void>;
  reset: () => void;
  updateField: (field: keyof LabelData, value: number | string) => void;
}

export function useJ30C(): J30CState & J30CActions {
  const [loading, setLoading] = useState(false);
  const [labelData, setLabelData] = useState<LabelData | null>(null);
  const [printLabels, setPrintLabels] = useState<PrintLabel[] | null>(null);
  const [queried, setQueried] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const lookupProduct = useCallback(async (req: LookupRequest) => {
    setLoading(true);
    setQueried(false);
    setNotFound(false);
    setPrintLabels(null);
    try {
      const resp = await j30cApi.lookup(req);
      if (resp.data) {
        setLabelData(resp.data);
        setNotFound(false);
      } else {
        setLabelData(null);
        setNotFound(true);
        message.warning(resp.message || '查無此產品代號!');
      }
      setQueried(true);
    } catch {
      message.error('查詢失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, []);

  const doPrint = useCallback(async (copies: number) => {
    if (!labelData) {
      message.warning('請先查詢產品資料');
      return;
    }
    setLoading(true);
    try {
      const resp = await j30cApi.print(labelData, copies);
      if (resp.data) {
        setPrintLabels(resp.data.labels);
        message.success(resp.data.message || '已產生標籤');
      }
    } catch {
      message.error('列印失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [labelData]);

  const reset = useCallback(() => {
    setLabelData(null);
    setPrintLabels(null);
    setQueried(false);
    setNotFound(false);
  }, []);

  const updateField = useCallback((field: keyof LabelData, value: number | string) => {
    setLabelData(prev => {
      if (!prev) return prev;
      const updated = { ...prev, [field]: value };
      // Auto-recalculate conversions (BR-C01, BR-C02, BR-C03)
      if (field === 'width_inch') {
        updated.width_cm = Math.round((value as number) * 2.54);
      }
      if (field === 'width2_inch') {
        updated.width2_cm = Math.round((value as number) * 2.54);
      }
      if (field === 'weight_gsm') {
        updated.weight_oz_yd = Math.round(((value as number) / 33.936) * 100) / 100;
      }
      return updated;
    });
  }, []);

  return {
    loading,
    labelData,
    printLabels,
    queried,
    notFound,
    lookupProduct,
    doPrint,
    reset,
    updateField,
  };
}
